import { useMemo } from "react";
import { useMyPublishers, useMyUnlinkedPublishers } from "@seabrookstudios/pitch2table";

/**
 * Merges publishers from useMyUnlinkedPublishers (base) with useMyPublishers (overrides)
 * Includes all publishers so shortlisted/contact-book publishers remain visible.
 * @typedef {object} MergedPublishersResult
 * @property {import('@seabrookstudios/pitch2table-core').PublisherWithContactBookPublisher[]} data
 * @property {() => Promise<void>} refetch
 * @property {boolean} isRefetching
 * @property {boolean} isLoading
 */

/**
 * @returns {MergedPublishersResult}
 */
export const useMergedPublishers = () => {
  const {
    data: myPublishers = [],
    refetch: refetchMyPublishers,
    isRefetching: isRefetchingMyPublishers,
    isLoading: isLoadingMyPublishers,
  } = useMyPublishers();
  const {
    data: allPublishers = [],
    refetch: refetchAllPublishers,
    isRefetching: isRefetchingAllPublishers,
    isLoading: isLoadingAllPublishers,
  } = useMyUnlinkedPublishers();

  /**
   * @type {import('@seabrookstudios/pitch2table-core').PublisherWithContactBookPublisher[]}
   */
  const publishers = useMemo(() => {
    if (isLoadingMyPublishers || isLoadingAllPublishers) {
      return [];
    }

    /** @type {Map<import('@seabrookstudios/pitch2table-core').PublisherId, [import('@seabrookstudios/pitch2table-core').PublisherForApp, import('@seabrookstudios/pitch2table-core').ContactBookPublisher|undefined]>} */
    const mergedById = new Map(allPublishers.map((publisher) => [publisher.id, [publisher, undefined]]));

    myPublishers.forEach((myPublisher) => {
      const existing = mergedById.get(myPublisher.id);

      if (existing) {
        mergedById.set(myPublisher.id, [existing[0], myPublisher]);
        return;
      }

      mergedById.set(myPublisher.id, [{ ...myPublisher }, myPublisher]);
    });

    return Array.from(mergedById.values());
  }, [allPublishers, myPublishers, isLoadingMyPublishers, isLoadingAllPublishers]);

  const refetch = async () => {
    await refetchMyPublishers();
    await refetchAllPublishers();
  };

  const isRefetching = isRefetchingMyPublishers || isRefetchingAllPublishers;
  const isLoading = isLoadingMyPublishers || isLoadingAllPublishers;

  return /** @type {{ data: import('@seabrookstudios/pitch2table-core').PublisherWithContactBookPublisher[], refetch: () => Promise<void>, isRefetching: boolean, isLoading: boolean }} */ ({
    data: publishers,
    refetch,
    isRefetching,
    isLoading,
  });
};
